import { useCallback, useEffect, useMemo, useState } from "react";
import { api, INR, fmtDMY, formatApiError } from "@/lib/api";
import { AuditTabs } from "@/pages/LoginAudit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ShieldCheck, Download, Search, User, FileText, Wand2, Undo2 } from "lucide-react";
import DatePicker from "@/components/DatePicker";
import Pagination from "@/components/Pagination";
import { toast } from "sonner";

const SOURCE_LABEL = {
  manual: "Recorded manually",
  csv_payment_import: "Payments CSV",
  csv_invoice_import: "Invoice CSV (paid on import)",
};

const SOURCE_ICON = {
  manual: User,
  csv_payment_import: FileText,
  csv_invoice_import: Wand2,
};

function SourcePill({ source }) {
  const label = SOURCE_LABEL[source] || source || "unknown";
  const Icon = SOURCE_ICON[source] || User;
  const tone =
    source === "manual"
      ? "bg-[color:var(--aisensy-green-soft)] text-[color:var(--aisensy-dark)]"
      : "bg-[color:var(--pending-bg)] text-[color:var(--pending)]";
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[11px] font-semibold ${tone}`}
      data-testid={`audit-source-${source}`}
    >
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

export default function AuditLogPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    q: "",
    source: "all",
    date_from: "",
    date_to: "",
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 2000 };
      if (filters.q) params.q = filters.q;
      if (filters.source !== "all") params.source = filters.source;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const { data } = await api.get("/audit/payments", { params });
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [filters, pageSize]);

  const undoBatch = async (batchId, filename) => {
    if (!batchId) {
      toast.error("This entry has no batch id (older import)");
      return;
    }
    const label = filename ? `“${filename}”` : `batch ${batchId.slice(0, 8)}…`;
    if (!window.confirm(
      `Undo the entire CSV drop ${label}?\n\nThis removes every payment recorded from that file across all invoices, then recomputes their status. Invoices themselves stay put.`
    )) return;
    try {
      const { data } = await api.post(`/audit/batches/${batchId}/undo`);
      toast.success(
        `Reverted ${data.payments_reverted} payment${data.payments_reverted === 1 ? "" : "s"} across ${data.invoices_affected} invoice${data.invoices_affected === 1 ? "" : "s"}`
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const paginatedRows = useMemo(() => {
    if (pageSize === "All" || pageSize === 0) return rows;
    const start = (page - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, page, pageSize]);

  const exportCsv = async () => {
    try {
      const params = {};
      if (filters.q) params.q = filters.q;
      if (filters.source !== "all") params.source = filters.source;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const res = await api.get("/audit/payments/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `payments_audit_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const summary = useMemo(() => {
    const bySource = { manual: 0, csv_payment_import: 0, csv_invoice_import: 0 };
    let total_amount = 0;
    for (const r of rows) {
      bySource[r.source] = (bySource[r.source] || 0) + 1;
      total_amount += r.amount || 0;
    }
    return { bySource, total_amount };
  }, [rows]);

  return (
    <div className="space-y-4" data-testid="audit-log-page">
      <div className="flex items-baseline gap-3">
        <div className="eyebrow">Reconciliation</div>
        <h1 className="font-display text-2xl leading-none">Payment Audit Log</h1>
      </div>
      <div className="text-sm text-ink-2 flex items-center justify-between gap-3 flex-wrap">
        <span>
          Every payment entry ever recorded — who logged it, when, and from which
          CSV file. Filter, search or export for accounting handover.
        </span>
        <AuditTabs />
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[color:var(--line)] border border-line rounded-sm overflow-hidden">
        <div className="bg-card p-4">
          <div className="eyebrow">Payments in view</div>
          <div className="text-2xl font-semibold tabular mt-1">{total}</div>
        </div>
        <div className="bg-card p-4">
          <div className="eyebrow">Total value</div>
          <div className="text-2xl font-semibold tabular mt-1">
            {INR(summary.total_amount)}
          </div>
        </div>
        <div className="bg-card p-4">
          <div className="eyebrow">Manual entries</div>
          <div className="text-2xl font-semibold tabular mt-1">
            {summary.bySource.manual || 0}
          </div>
        </div>
        <div className="bg-card p-4">
          <div className="eyebrow">From CSV imports</div>
          <div className="text-2xl font-semibold tabular mt-1">
            {(summary.bySource.csv_payment_import || 0) +
              (summary.bySource.csv_invoice_import || 0)}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div
        className="border border-line bg-card p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3"
        data-testid="audit-filters"
      >
        <div className="md:col-span-2">
          <Label className="eyebrow">Search</Label>
          <div className="relative mt-1">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-ink-2" />
            <Input
              className="rounded-sm pl-7"
              placeholder="Invoice #, UTR, Payment ID, CSV filename, recorder"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              data-testid="audit-search"
            />
          </div>
        </div>
        <div>
          <Label className="eyebrow">Source</Label>
          <Select
            value={filters.source}
            onValueChange={(v) => setFilters({ ...filters, source: v })}
          >
            <SelectTrigger className="rounded-sm mt-1" data-testid="audit-source-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              <SelectItem value="manual">Recorded manually</SelectItem>
              <SelectItem value="csv_payment_import">Payments CSV</SelectItem>
              <SelectItem value="csv_invoice_import">Invoice CSV (paid on import)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="eyebrow">From date</Label>
          <DatePicker
            value={filters.date_from}
            onChange={(v) => setFilters({ ...filters, date_from: v || "" })}
            testId="audit-date-from"
            placeholder="Any"
          />
        </div>
        <div>
          <Label className="eyebrow">To date</Label>
          <DatePicker
            value={filters.date_to}
            onChange={(v) => setFilters({ ...filters, date_to: v || "" })}
            testId="audit-date-to"
            placeholder="Any"
          />
        </div>
      </div>

      {/* Table */}
      <div className="border border-line bg-card rounded-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-line">
          <div className="text-sm">
            {loading ? (
              <span className="text-ink-2">Loading audit log…</span>
            ) : (
              <>
                <span className="font-semibold">{rows.length}</span>{" "}
                <span className="text-ink-2">of {total} payments</span>
              </>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            disabled={rows.length === 0}
            className="rounded-sm bg-white text-ink hover:bg-[color:var(--bone)] hover:text-ink"
            data-testid="audit-export"
          >
            <Download className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm tabular" data-testid="audit-table">
            <thead className="bg-[color:var(--bone)] text-ink-2">
              <tr>
                <th className="text-left px-3 py-2">Paid on</th>
                <th className="text-left px-3 py-2">Invoice</th>
                <th className="text-left px-3 py-2">Entity</th>
                <th className="text-right px-3 py-2">Amount</th>
                <th className="text-left px-3 py-2">Reference</th>
                <th className="text-left px-3 py-2">Source</th>
                <th className="text-left px-3 py-2">Recorded by</th>
                <th className="text-left px-3 py-2">When</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={9} className="px-3 py-6 text-center text-ink-2">
                    No payments match the current filters.
                  </td>
                </tr>
              )}
              {paginatedRows.map((r, idx) => (
                <tr
                  key={r.payment_id_local || idx}
                  className="border-t border-line hover:bg-[color:var(--bone)]"
                  data-testid={`audit-row-${idx}`}
                >
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.payment_date ? fmtDMY(r.payment_date) : "-"}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap font-semibold">
                    {r.invoice_number}
                  </td>
                  <td className="px-3 py-2">
                    <div>{r.entity_name || "—"}</div>
                    <div className="text-[10px] text-ink-2 uppercase">
                      {r.entity_type}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap font-semibold">
                    {INR(r.amount)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {(() => {
                      if (r.utr_number) return <>UTR {r.utr_number}</>;
                      if (r.payment_ref_id) return <>ID {r.payment_ref_id}</>;
                      return <span className="text-ink-2">—</span>;
                    })()}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <SourcePill source={r.source} />
                    {r.csv_filename && (
                      <div className="text-[10px] text-ink-2 mt-0.5 max-w-[180px] truncate">
                        {r.csv_filename}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.recorded_by_name || "—"}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-ink-2">
                    {r.recorded_at ? r.recorded_at.slice(0, 16).replace("T", " ") : "—"}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {r.source && r.source !== "manual" && r.import_batch_id ? (
                      <button
                        onClick={() => undoBatch(r.import_batch_id, r.csv_filename)}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-[color:var(--overdue)] hover:bg-[color:var(--overdue-bg)] px-2 py-1 rounded-sm"
                        data-testid={`audit-undo-${idx}`}
                        title={`Undo the entire CSV drop ${r.csv_filename ? `“${r.csv_filename}”` : ""}`}
                      >
                        <Undo2 className="w-3 h-3" /> Undo batch
                      </button>
                    ) : (
                      <span className="text-[11px] text-ink-2">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length > 0 && (
          <Pagination
            total={rows.length}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            entityLabel="payments"
          />
        )}
      </div>
      <div className="text-[11px] text-ink-2 flex items-center gap-1">
        <ShieldCheck className="w-3 h-3" />
        Every payment above is stitched back to its original invoice — click{" "}
        <span className="font-semibold text-ink">Invoices</span> and search the
        invoice number for the full timeline.
      </div>
    </div>
  );
}
